import React, { useState, useRef, useEffect } from 'react';
import styled, { keyframes, css } from 'styled-components';
import { BRAND_GREEN_PRIMARY } from 'resources/constants/colors';
import useWellness from 'state/wellness';
import { API } from 'resources/constants/urls';

// ---------------------------------------------------------------------------
// Animations
// ---------------------------------------------------------------------------

const fadeUp = keyframes`
    from { opacity: 0; transform: translateY(10px); }
    to   { opacity: 1; transform: translateY(0); }
`;

const breatheExpand = keyframes`
    0%   { transform: scale(1); opacity: 0.7; }
    50%  { transform: scale(1.4); opacity: 1; }
    100% { transform: scale(1); opacity: 0.7; }
`;

const fadeIn = keyframes`
    from { opacity: 0; }
    to   { opacity: 1; }
`;

const slideUp = keyframes`
    from { opacity: 0; transform: translateY(30px); }
    to   { opacity: 1; transform: translateY(0); }
`;

// ---------------------------------------------------------------------------
// Carrusel data
// ---------------------------------------------------------------------------

const KIT_ITEMS = [
    {
        id: 'breathing',
        icon: '🌬️',
        title: 'Respira conmigo',
        desc: 'Ejercicio 4-7-8',
        duration: '1 min',
        bg: '#E3F2FD',
        accent: '#1976d2',
        type: 'breathing',
    },
    {
        id: 'grounding',
        icon: '🌿',
        title: 'Aterriza en el presente',
        desc: 'Técnica 5-4-3-2-1',
        duration: '2 min',
        bg: '#f1f8e9',
        accent: '#7cb342',
        type: 'grounding',
    },
    {
        id: 'audio',
        icon: '🎵',
        title: 'Sonido para calmar',
        desc: 'Ruido blanco suave',
        duration: '5 min',
        bg: '#EDE7F6',
        accent: '#7e57c2',
        type: 'audio',
    },
    {
        id: 'body',
        icon: '🚶',
        title: 'Mueve tu cuerpo',
        desc: 'Estiramiento rápido',
        duration: '3 min',
        bg: '#FFF3E0',
        accent: '#ef8c35',
        type: 'body',
    },
];

// ---------------------------------------------------------------------------
// Grounding text steps
// ---------------------------------------------------------------------------

const GROUNDING_STEPS = [
    { n: 5, sense: '👁️ VE', instruction: 'Nombra 5 cosas que puedes ver ahora mismo a tu alrededor.' },
    { n: 4, sense: '✋ TOCA', instruction: 'Nombra 4 texturas que puedes sentir (tu ropa, la silla, el aire).' },
    { n: 3, sense: '👂 ESCUCHA', instruction: 'Escucha con atención. ¿Qué 3 sonidos puedes identificar?' },
    { n: 2, sense: '👃 HUELE', instruction: 'Aspira despacio. ¿Qué 2 aromas percibes?' },
    { n: 1, sense: '👅 SABOREA', instruction: 'Detecta 1 sabor en tu boca. Solo observa sin juzgar.' },
];

// ---------------------------------------------------------------------------
// Breathing phases
// ---------------------------------------------------------------------------

const BREATH_PHASES = [
    { label: 'Inhala', seconds: 4, scale: 1.4 },
    { label: 'Retén', seconds: 7, scale: 1.4 },
    { label: 'Exhala', seconds: 8, scale: 1.0 },
];

// ---------------------------------------------------------------------------
// Styled components — carrusel
// ---------------------------------------------------------------------------

const SectionLabel = styled.p`
    font-size: 12px;
    font-weight: 700;
    text-transform: uppercase;
    letter-spacing: 0.7px;
    color: #aaa;
    margin: 0 0 10px;
    padding: 0 2px;
`;

const Scroll = styled.div`
    display: flex;
    gap: 12px;
    overflow-x: auto;
    padding-bottom: 6px;
    -ms-overflow-style: none;
    scrollbar-width: none;
    &::-webkit-scrollbar { display: none; }
    animation: ${fadeUp} 0.4s 0.2s ease both;
`;

const KitCard = styled.button`
    flex-shrink: 0;
    width: 130px;
    background: ${({ bg }) => bg};
    border: none;
    border-radius: 20px;
    padding: 16px 14px;
    text-align: left;
    cursor: pointer;
    -webkit-tap-highlight-color: transparent;
    transition: transform 0.15s ease;
    display: flex;
    flex-direction: column;
    gap: 8px;

    &:active {
        transform: scale(0.96);
    }
`;

const KitIcon = styled.div`
    font-size: 28px;
    line-height: 1;
`;

const KitTitle = styled.p`
    font-size: 13px;
    font-weight: 700;
    color: #1a1a1a;
    margin: 0;
    line-height: 1.3;
`;

const KitDesc = styled.p`
    font-size: 11px;
    color: #666;
    margin: 0;
`;

const KitDuration = styled.span`
    font-size: 10px;
    font-weight: 700;
    color: ${({ accent }) => accent};
    background: ${({ accent }) => `${accent}18`};
    border-radius: 20px;
    padding: 2px 8px;
    display: inline-block;
`;

// ---------------------------------------------------------------------------
// Modal overlay
// ---------------------------------------------------------------------------

const Overlay = styled.div`
    position: fixed;
    inset: 0;
    background: rgba(0, 0, 0, 0.5);
    z-index: 300;
    display: flex;
    align-items: flex-end;
    animation: ${fadeIn} 0.2s ease;
`;

const Modal = styled.div`
    background: #fff;
    border-radius: 24px 24px 0 0;
    padding: 20px 20px 50px;
    width: 100%;
    max-width: 480px;
    margin: 0 auto;
    animation: ${slideUp} 0.3s ease;
    max-height: 90vh;
    overflow-y: auto;
`;

const ModalHandle = styled.div`
    width: 40px;
    height: 4px;
    background: #e0e0e0;
    border-radius: 2px;
    margin: 0 auto 20px;
`;

const ModalClose = styled.button`
    position: absolute;
    top: 20px;
    right: 20px;
    background: #f5f5f5;
    border: none;
    border-radius: 50%;
    width: 34px;
    height: 34px;
    font-size: 16px;
    cursor: pointer;
    display: flex;
    align-items: center;
    justify-content: center;
`;

const ModalContainer = styled.div`
    position: relative;
`;

// ---------------------------------------------------------------------------
// Breathing exercise UI
// ---------------------------------------------------------------------------

const BreathingWrap = styled.div`
    display: flex;
    flex-direction: column;
    align-items: center;
    padding: 10px 0 20px;
    gap: 24px;
`;

const BreathingCircle = styled.div`
    width: 140px;
    height: 140px;
    border-radius: 50%;
    background: linear-gradient(135deg, #bbdefb, #90caf9);
    display: flex;
    align-items: center;
    justify-content: center;
    flex-direction: column;
    gap: 4px;
    ${({ $active, $phase }) => $active && css`
        animation: ${breatheExpand} ${BREATH_PHASES[$phase]?.seconds || 4}s ease-in-out infinite;
    `}
`;

const BreathPhaseLabel = styled.p`
    font-size: 16px;
    font-weight: 800;
    color: #0d47a1;
    margin: 0;
`;

const BreathCounter = styled.p`
    font-size: 28px;
    font-weight: 900;
    color: #0d47a1;
    margin: 0;
`;

const BreathInstructions = styled.p`
    font-size: 13px;
    color: #555;
    text-align: center;
    line-height: 1.6;
    margin: 0;
`;

const BigButton = styled.button`
    width: 100%;
    background: ${({ bg }) => bg || BRAND_GREEN_PRIMARY};
    color: ${({ textColor }) => textColor || '#fff'};
    border: none;
    border-radius: 16px;
    padding: 16px;
    font-size: 16px;
    font-weight: 700;
    cursor: pointer;
    -webkit-tap-highlight-color: transparent;

    &:active { opacity: 0.85; }
`;

// ---------------------------------------------------------------------------
// Grounding exercise UI
// ---------------------------------------------------------------------------

const GroundingStep = styled.div`
    background: ${({ bg }) => bg || '#f5f5f5'};
    border-radius: 16px;
    padding: 16px;
    margin-bottom: 12px;
    animation: ${fadeUp} 0.3s ease;
`;

const GroundingN = styled.div`
    font-size: 36px;
    font-weight: 900;
    color: #7cb342;
    line-height: 1;
    margin-bottom: 4px;
`;

const GroundingSense = styled.p`
    font-size: 12px;
    font-weight: 700;
    color: #7cb342;
    letter-spacing: 1px;
    margin: 0 0 6px;
`;

const GroundingInstruction = styled.p`
    font-size: 14px;
    color: #333;
    margin: 0;
    line-height: 1.5;
`;

// ---------------------------------------------------------------------------
// Audio player UI
// ---------------------------------------------------------------------------

const AudioWrap = styled.div`
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 20px;
    padding: 20px 0;
`;

const AudioWave = styled.div`
    font-size: 60px;
    line-height: 1;
    ${({ $playing }) => $playing && css`
        animation: ${breatheExpand} 2s ease-in-out infinite;
    `}
`;

const AudioNote = styled.p`
    font-size: 13px;
    color: #777;
    text-align: center;
    line-height: 1.6;
    margin: 0;
`;

// ---------------------------------------------------------------------------
// Body movement UI
// ---------------------------------------------------------------------------

const BodyStep = styled.div`
    background: #FFF3E0;
    border-radius: 16px;
    padding: 16px;
    margin-bottom: 12px;
`;

const BodyStepTitle = styled.p`
    font-size: 14px;
    font-weight: 700;
    color: #ef8c35;
    margin: 0 0 4px;
`;

const BodyStepDesc = styled.p`
    font-size: 13px;
    color: #555;
    margin: 0;
`;

const BODY_STEPS = [
    { title: '1. Rueda los hombros', desc: 'Lleva los hombros hacia las orejas, rúédalos hacia atrás 5 veces. Suelta la tensión.' },
    { title: '2. Estira el cuello', desc: 'Inclina la cabeza hacia un lado, sostén 10 segundos. Cambia de lado.' },
    { title: '3. Abre el pecho', desc: 'Entrelaza los dedos detrás de tu espalda. Abre el pecho hacia arriba. Respira hondo.' },
    { title: '4. Sacude las manos', desc: 'Sacude las muñecas y manos durante 30 segundos. Libera la tensión acumulada.' },
    { title: '5. Pie en la tierra', desc: 'Párate. Siente el piso bajo tus pies. Transfiere el peso de un pie al otro lentamente.' },
];

// ---------------------------------------------------------------------------
// Breathing hook
// ---------------------------------------------------------------------------

const useBreathing = () => {
    const [active, setActive] = useState(false);
    const [phase, setPhase] = useState(0);
    const [count, setCount] = useState(BREATH_PHASES[0].seconds);
    const timerRef = useRef(null);
    const phaseRef = useRef(0);
    const countRef = useRef(BREATH_PHASES[0].seconds);

    const tick = () => {
        countRef.current -= 1;
        setCount(countRef.current);
        if (countRef.current <= 0) {
            phaseRef.current = (phaseRef.current + 1) % BREATH_PHASES.length;
            countRef.current = BREATH_PHASES[phaseRef.current].seconds;
            setPhase(phaseRef.current);
            setCount(countRef.current);
        }
    };

    const start = () => {
        phaseRef.current = 0;
        countRef.current = BREATH_PHASES[0].seconds;
        setPhase(0);
        setCount(BREATH_PHASES[0].seconds);
        setActive(true);
        timerRef.current = setInterval(tick, 1000);
    };

    const stop = () => {
        clearInterval(timerRef.current);
        setActive(false);
        setPhase(0);
        setCount(BREATH_PHASES[0].seconds);
        phaseRef.current = 0;
        countRef.current = BREATH_PHASES[0].seconds;
    };

    return { active, phase, count, start, stop };
};

// ---------------------------------------------------------------------------
// Modal content by type
// ---------------------------------------------------------------------------

const BreathingModal = () => {
    const { active, phase, count, start, stop } = useBreathing();
    const currentPhase = BREATH_PHASES[phase];
    return (
        <BreathingWrap>
            <BreathInstructions>
                Sigue el círculo. Inhala 4s, retén 7s y exhala 8s.{' '}
                Técnica 4-7-8 para calmar el sistema nervioso.
            </BreathInstructions>
            <BreathingCircle $active={active} $phase={phase}>
                <BreathPhaseLabel>{active ? currentPhase.label : '4-7-8'}</BreathPhaseLabel>
                <BreathCounter>{active ? count : '😮‍💨'}</BreathCounter>
            </BreathingCircle>
            {active ? (
                <BigButton bg="#ef5350" onClick={stop}>Detener</BigButton>
            ) : (
                <BigButton bg="#1976d2" onClick={start}>Iniciar ejercicio</BigButton>
            )}
        </BreathingWrap>
    );
};

const GroundingModal = () => (
    <div style={{ paddingTop: 8 }}>
        <p style={{ fontSize: 13, color: '#666', marginBottom: 16, lineHeight: 1.6 }}>
            Este ejercicio ancla tu mente al momento presente usando tus 5 sentidos. Tómate tu tiempo en cada paso.
        </p>
        {GROUNDING_STEPS.map((step, i) => (
            <GroundingStep key={i} bg={i % 2 === 0 ? '#f1f8e9' : '#e8f5e9'}>
                <GroundingN>{step.n}</GroundingN>
                <GroundingSense>{step.sense}</GroundingSense>
                <GroundingInstruction>{step.instruction}</GroundingInstruction>
            </GroundingStep>
        ))}
    </div>
);

const AudioModal = () => {
    const [playing, setPlaying] = useState(false);
    const audioRef = useRef(null);

    useEffect(() => {
        return () => {
            if (audioRef.current) {
                audioRef.current.pause();
                audioRef.current = null;
            }
        };
    }, []);

    const toggleAudio = () => {
        if (playing) {
            audioRef.current?.pause();
            setPlaying(false);
        } else {
            if (!audioRef.current) {
                audioRef.current = new Audio(`${API}/public/audio/white-noise.mp3`);
                audioRef.current.loop = true;
            }
            audioRef.current.play().catch(() => {});
            setPlaying(true);
        }
    };

    return (
        <AudioWrap>
            <AudioWave $playing={playing}>🎵</AudioWave>
            <AudioNote>
                Cierra los ojos. Deja que el sonido te rodee.{'\n'}
                No hay nada que hacer, solo escuchar.
            </AudioNote>
            <BigButton
                bg={playing ? '#ef5350' : '#7e57c2'}
                onClick={toggleAudio}
            >
                {playing ? '⏸ Pausar' : '▶ Reproducir sonido blanco'}
            </BigButton>
            {playing && (
                <p style={{ fontSize: 12, color: '#aaa', textAlign: 'center' }}>
                    🔈 Activa el volumen de tu dispositivo
                </p>
            )}
        </AudioWrap>
    );
};

const BodyModal = () => (
    <div style={{ paddingTop: 8 }}>
        <p style={{ fontSize: 13, color: '#666', marginBottom: 16, lineHeight: 1.6 }}>
            Tómate 3 minutos. Sigue cada paso despacio y sin forzar.
        </p>
        {BODY_STEPS.map((step, i) => (
            <BodyStep key={i}>
                <BodyStepTitle>{step.title}</BodyStepTitle>
                <BodyStepDesc>{step.desc}</BodyStepDesc>
            </BodyStep>
        ))}
    </div>
);

const MODAL_CONTENT = {
    breathing: { title: '🌬️ Respiración guiada', Component: BreathingModal },
    grounding: { title: '🌿 Aterriza en el presente', Component: GroundingModal },
    audio:     { title: '🎵 Sonido para calmar', Component: AudioModal },
    body:      { title: '🚶 Mueve tu cuerpo', Component: BodyModal },
};

// ---------------------------------------------------------------------------
// Main component
// ---------------------------------------------------------------------------

const FirstAidKit = () => {
    const [activeItem, setActiveItem] = useState(null);
    const [, wellnessDispatcher] = useWellness();
    const openTimeRef = useRef(null);
    const modal = activeItem ? MODAL_CONTENT[activeItem.type] : null;

    const handleOpen = (item) => {
        setActiveItem(item);
        openTimeRef.current = Date.now();
    };

    const handleClose = () => {
        // Track activity completion
        if (activeItem && openTimeRef.current) {
            const durationSeconds = Math.round((Date.now() - openTimeRef.current) / 1000);
            wellnessDispatcher.saveActivityStart({
                activityType: activeItem.type === 'body' ? 'stretching' : activeItem.type,
                durationSeconds,
                completed: durationSeconds >= 10,
            });
        }
        setActiveItem(null);
        openTimeRef.current = null;
    };

    return (
        <>
            <div>
                <SectionLabel>Botiquín de primeros auxilios</SectionLabel>
                <Scroll>
                    {KIT_ITEMS.map((item) => (
                        <KitCard
                            key={item.id}
                            bg={item.bg}
                            onClick={() => handleOpen(item)}
                        >
                            <KitIcon>{item.icon}</KitIcon>
                            <KitTitle>{item.title}</KitTitle>
                            <KitDesc>{item.desc}</KitDesc>
                            <KitDuration accent={item.accent}>{item.duration}</KitDuration>
                        </KitCard>
                    ))}
                </Scroll>
            </div>

            {activeItem && modal && (
                <Overlay onClick={handleClose}>
                    <Modal onClick={(e) => e.stopPropagation()}>
                        <ModalContainer>
                            <ModalHandle />
                            <ModalClose onClick={handleClose}>✕</ModalClose>
                            <h2 style={{ fontSize: 18, fontWeight: 800, color: '#1a1a1a', margin: '0 0 16px' }}>
                                {modal.title}
                            </h2>
                            <modal.Component />
                        </ModalContainer>
                    </Modal>
                </Overlay>
            )}
        </>
    );
};

export default FirstAidKit;
